import { useMemo, useState } from 'react'
import {
	Alert,
	Autocomplete,
	AppBar,
	Box,
	IconButton,
	TextField,
	Toolbar,
	CircularProgress
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { useSelector } from 'react-redux'
import { debounce } from 'lodash'

import { HEADER_HEIGHT, THEMES } from '../../constants'
import useSettings from '../../hooks/useSettings'
import MenuIcon from '../../icons/Menu'
import AccountPopover from './AccountPopover'
import TerminalPopover from './TerminalPopover'
import NotificationsPopover from './NotificationsPopover'
import ChatPopover from './ChatPopover'
import Moon from '../../icons/Moon'
import Sun from '../../icons/Sun'
import { queryPractices, useClinicSelect } from '../../util/unified'
import { useCurrentPractice } from '../../contexts/PracticeContext'
import { isCheckOnlyPractice } from '../../util/practice'
import { isScratchAdminUser } from '../../util/user'
import { useReader } from '../../contexts/ReaderContext'
import { selectContext } from '../../selectors/auth'
import { useConsolidatedPractice } from '../../hooks/useConsolidatedPracticeAsSource'

const useStyles = makeStyles()(() => ({
	locationPicker: {
		'& .MuiOutlinedInput-root': {
			fontSize: '12px',
			color: 'inherit'
		},
		'&:not(.Mui-focused)': {
			width: 'auto',
			minWidth: '200px',
			'& .MuiOutlinedInput-notchedOutline': {
				borderWidth: 0
			},
			'& input': {
				fontSize: 'inherit'
			}
		},
		'& + .MuiAutocomplete-popper': {
			width: '350px !important'
		}
	}
}))

const DashboardNavbarRoot = styled(AppBar)(({ theme }) => ({
	...(theme.palette.mode === 'light' && {
		backgroundColor: (theme.palette.background as any).secondary, // TODO TSC theme
		color: theme.palette.text.secondary
	}),
	...(theme.palette.mode === 'dark' && {
		backgroundColor: (theme.palette.background as any).secondary, // TODO TSC theme
		borderBottom: `1px solid ${theme.palette.divider}`
	}),
	zIndex: theme.zIndex.drawer + 100
}))

interface DashboardNavbarProps {
	onSidebarMobileOpen?: (...args: any[]) => any
}

const DashboardNavbar = (props: DashboardNavbarProps) => {
	const { onSidebarMobileOpen, ...other } = props
	const { settings, saveSettings } = useSettings()
	const context = useSelector(selectContext)
	const { user } = context
	const { currentPracticeObject, setPracticeID, isPaymentEnabled } = useCurrentPractice()

	// TO DO: MAKE SURE ALL USERS WHERE isAdmin === true HAVE consolidatorId
	const [darkMode, setDarkMode] = useState(settings.theme === 'dark')
	const [isLoadingAdminPractices, setIsLoadingAdminPractices] = useState(false)

	const { unsavedChanges } = useReader()
	const useConsolidated = useConsolidatedPractice()

	// Use dynamic loading for practice selector
	const dynamicPracticesLoad = user?.isScratchAdmin

	const {
		status: organizationsStatus,
		data: organizations,
		error: organizationsError
	} = useClinicSelect(context)

	// Only used for scratch admins
	const [options, setOptions] = useState<IPractice[]>([])

	const [value, setValue] = useState()
	const { classes } = useStyles()

	const handleSwitchTheme = () => {
		saveSettings({
			...settings,
			theme: settings.theme === 'light' ? THEMES.DARK : THEMES.LIGHT
		})

		setDarkMode(settings.theme === 'light')
	}

	// Do type-ahead style practice load for scratch admins instead of pre-loading.
	const loadAdminPractices = useMemo(
		() =>
			debounce(async (userInput: string | undefined) => {
				if (dynamicPracticesLoad) {
					if (userInput === '' || userInput === undefined) {
						setOptions(value || currentPracticeObject ? [value || currentPracticeObject] : [])
						return undefined
					}

					queryPractices(
						userInput,
						options,
						setOptions,
						[],
						setIsLoadingAdminPractices,
						useConsolidated
					)
				}
			}, 400),
		[dynamicPracticesLoad, useConsolidated]
	)

	return (
		<DashboardNavbarRoot {...other} elevation={0}>
			<Toolbar
				disableGutters
				sx={{
					alignItems: 'center',
					display: 'flex',
					height: HEADER_HEIGHT,
					px: 3,
					py: 1
				}}
			>
				<Box sx={{ display: { lg: 'none' } }}>
					<IconButton color="inherit" onClick={onSidebarMobileOpen} size="large">
						<MenuIcon fontSize="small" />
					</IconButton>
				</Box>
				{organizationsError && (
					<Box mb={3}>
						<Alert severity="error">{organizationsError.message}</Alert>
					</Box>
				)}
				{organizationsStatus !== 'loading' &&
					organizations &&
					organizations.length === 0 &&
					currentPracticeObject &&
					!dynamicPracticesLoad && (
						<TextField
							label={`${user?.primaryClinicName}`}
							sx={{ width: '30ch' }}
							size="small"
							disabled
						/>
					)}
				{organizationsStatus !== 'loading' &&
					organizations &&
					organizations.length > 0 &&
					currentPracticeObject &&
					!dynamicPracticesLoad && (
						<Autocomplete
							data-testid="practice-selector"
							disabled={unsavedChanges}
							className={classes.locationPicker}
							disableClearable
							disablePortal
							autoComplete
							groupBy={(option) =>
								(option.clinicName
									? option.clinicName[0].toUpperCase()
									: option.clinicCountry || option.id) || ''
							}
							options={organizations}
							getOptionLabel={(option) =>
								`${option.clinicName}  (${option.clinicCity}, ${option.clinicState})`
							}
							isOptionEqualToValue={(option, newValue) =>
								option.id ? option.id === newValue.id : false
							}
							fullWidth
							sx={{ maxWidth: 350 }}
							value={value || currentPracticeObject}
							onChange={(event, newValue: any) => {
								if (typeof newValue === 'string') {
									setTimeout(() => null)
								} else if (newValue && newValue.inputValue) {
									setTimeout(() => null)
								} else {
									setPracticeID(newValue.id)
									setValue(newValue)
								}
							}}
							renderInput={(params) => <TextField {...params} />}
						/>
					)}
				{currentPracticeObject && dynamicPracticesLoad && (
					<Autocomplete
						disabled={unsavedChanges}
						className={classes.locationPicker}
						disableClearable
						disablePortal
						autoComplete
						filterOptions={(x) => x}
						groupBy={(option) =>
							(option.clinicName
								? option.clinicName[0].toUpperCase()
								: option.clinicCountry || option.id) || ''
						}
						options={options}
						getOptionLabel={(option) =>
							option.clinicName
								? `${option.clinicName}  (${option.clinicCity}, ${option.clinicState})`
								: ''
						}
						isOptionEqualToValue={(option, newValue) => option.id === newValue.id}
						noOptionsText="Type to search"
						fullWidth
						sx={{ maxWidth: 350 }}
						value={value || currentPracticeObject}
						onChange={(event, newValue: any) => {
							if (typeof newValue === 'string') {
								setTimeout(() => null)
							} else if (newValue && newValue.inputValue) {
								setTimeout(() => null)
							} else {
								setPracticeID(newValue.id)
								setValue(newValue)
							}
						}}
						onInputChange={(event, newInputValue, reason) => {
							if (reason === 'input') {
								loadAdminPractices(newInputValue)
							}
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Pick a practice"
								slotProps={{
									input: {
										...params.InputProps,
										endAdornment: (
											<>
												{isLoadingAdminPractices && (
													<CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
												)}
												{params.InputProps.endAdornment}
											</>
										)
									}
								}}
							/>
						)}
					/>
				)}
				<Box
					sx={{
						flexGrow: 1,
						ml: 2
					}}
				/>
				<Box sx={{ ml: 1 }}>
					<IconButton color="inherit" onClick={handleSwitchTheme} size="large">
						{darkMode ? <Sun /> : <Moon />}
					</IconButton>
				</Box>
				{(isScratchAdminUser(user) || currentPracticeObject?.isChatEnabled) && (
					<Box sx={{ ml: 1 }}>
						<ChatPopover />
					</Box>
				)}
				{isPaymentEnabled && !isCheckOnlyPractice(currentPracticeObject) && (
					<Box sx={{ ml: 1 }}>
						<TerminalPopover />
					</Box>
				)}
				{isPaymentEnabled && (
					<Box sx={{ ml: 1 }}>
						<NotificationsPopover />
					</Box>
				)}

				<Box sx={{ ml: 2 }}>
					<AccountPopover />
				</Box>
			</Toolbar>
		</DashboardNavbarRoot>
	)
}

export default DashboardNavbar
